import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import { ApiError } from '../api/client';
import { exportsApi, paymentsApi } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { ExportMenu } from '../components/ExportMenu';
import { TableSkeleton } from '../components/TableSkeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { RecordPaymentDialog } from '../features/payments/RecordPaymentDialog';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useToast } from '../app/ToastProvider';
import { clientLabel } from '../utils/client';
import { formatDate, formatPeso } from '../utils/format';
import type { PaymentListItem } from '../api/types';

const METHOD_OPTIONS = [
  { value: '', label: 'All methods' },
  { value: 'CASH', label: 'Cash' },
  { value: 'GCASH', label: 'GCash' },
  { value: 'MAYA', label: 'Maya' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
];

const COLUMN_COUNT = 8;

export function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [recordOpen, setRecordOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState<PaymentListItem | null>(null);

  const toast = useToast();
  const queryClient = useQueryClient();
  const debouncedSearch = useDebouncedValue(search);

  const filters = {
    search: debouncedSearch || undefined,
    paymentMethod: paymentMethod || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  const queryParams = { page: page + 1, pageSize, ...filters };

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['payments', queryParams],
    queryFn: () => paymentsApi.list(queryParams),
    placeholderData: keepPreviousData,
  });

  const voidMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => paymentsApi.void(id, reason),
    onSuccess: async ({ message }) => {
      toast.success(message);
      setVoidTarget(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['payments'] }),
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['client'] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.message : 'The payment could not be voided.');
    },
  });

  return (
    <>
      <PageHeader
        title="Payments"
        description="Record payments and review the permanent payment history."
        actions={
          <>
            <ExportMenu
              onExport={(format) => exportsApi.payments(format, filters)}
              disabled={(data?.items.length ?? 0) === 0}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRecordOpen(true)}>
              Record Payment
            </Button>
          </>
        }
      />

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Payments could not be loaded.'}
        </Alert>
      ) : null}

      <Card>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ p: 2 }}
          alignItems={{ md: 'center' }}
        >
          <TextField
            placeholder="Search payment or client…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            sx={{ maxWidth: { md: 320 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            label="Payment Method"
            value={paymentMethod}
            onChange={(event) => {
              setPaymentMethod(event.target.value);
              setPage(0);
            }}
            sx={{ maxWidth: { md: 200 } }}
          >
            {METHOD_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="From"
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(0);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: { md: 170 } }}
          />
          <TextField
            label="To"
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(0);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: { md: 170 } }}
          />
        </Stack>

        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Payment Number</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Applied To</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableSkeleton columns={COLUMN_COUNT} />
              ) : data && data.items.length > 0 ? (
                data.items.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>{payment.paymentNumber}</TableCell>
                    <TableCell>
                      <Link component={RouterLink} to={`/clients/${payment.client.id}`}>
                        {clientLabel(payment.client)}
                      </Link>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {payment.client.clientNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>
                      <StatusBadge status={payment.paymentMethod} />
                      {payment.referenceNumber ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {payment.referenceNumber}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {payment.isVoid || payment.applications.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          {payment.isVoid ? '—' : 'Advance credit'}
                        </Typography>
                      ) : (
                        payment.applications.map((application) => (
                          <Typography variant="caption" display="block" key={application.invoice.id}>
                            {application.invoice.invoiceNumber} ·{' '}
                            {formatPeso(application.appliedAmount)}
                          </Typography>
                        ))
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          textDecoration: payment.isVoid ? 'line-through' : 'none',
                          color: payment.isVoid ? 'text.disabled' : 'inherit',
                        }}
                      >
                        {formatPeso(payment.amountReceived)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.isVoid ? (
                        <Tooltip title={payment.voidReason ?? ''}>
                          <span>
                            <StatusBadge status="VOID" />
                          </span>
                        </Tooltip>
                      ) : (
                        <StatusBadge status="PAID" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        startIcon={<BlockIcon />}
                        disabled={payment.isVoid}
                        onClick={() => setVoidTarget(payment)}
                      >
                        Void
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} sx={{ border: 0 }}>
                    <EmptyState
                      message="No payments found."
                      hint="Adjust the filters, or record a payment to get started."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data?.meta.total ?? 0}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <RecordPaymentDialog open={recordOpen} onClose={() => setRecordOpen(false)} />

      <ConfirmDialog
        open={voidTarget !== null}
        title={`Void ${voidTarget?.paymentNumber ?? 'payment'}?`}
        description="The payment record is kept permanently. Its allocations are reversed, affected invoices are recalculated, and any due-date advancement is undone."
        confirmLabel="Void Payment"
        danger
        reasonLabel="Reason for voiding"
        busy={voidMutation.isPending}
        onCancel={() => setVoidTarget(null)}
        onConfirm={(reason) => {
          if (voidTarget) voidMutation.mutate({ id: voidTarget.id, reason });
        }}
      />
    </>
  );
}

